import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Mic, Square, PhoneCall, Phone, Trophy, Lightbulb, CheckCircle2, ArrowRight, RotateCcw, Building2, Volume2 } from "lucide-react";
import { toast } from "sonner";

export default function ARSimulator() {
  const { data, isLoading } = useQuery({
    queryKey: ["ar-scenarios"],
    queryFn: async () => (await api.get("/ar-sim/scenarios")).data,
  });
  const { data: histData, refetch } = useQuery({
    queryKey: ["ar-results"],
    queryFn: async () => (await api.get("/ar-sim/results")).data,
  });
  const [active, setActive] = useState(null);

  if (isLoading)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (active) return <CallSim scenario={active} onDone={refetch} onExit={() => setActive(null)} />;

  const scenarios = data?.scenarios || [];
  const results = histData?.results || [];

  return (
    <div className="space-y-6" data-testid="ar-sim-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Healthcare BPO · Role-Play</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">AR Call Simulator</h1>
        <p className="text-muted-foreground mt-1">Handle a mock payer call on a claim denial. Speak your responses and get AI scoring on your denial handling.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {scenarios.map((s) => (
          <div key={s.id} className="p-6 rounded-md border border-border bg-card flex flex-col hover:-translate-y-1 transition-transform" data-testid={`scenario-${s.id}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center"><PhoneCall className="w-5 h-5 text-primary" /></div>
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-destructive/10 text-destructive">{s.denial_code}</span>
            </div>
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><Building2 className="w-3 h-3" /> {s.payer} · {s.difficulty}</span>
            <h3 className="font-heading font-bold text-lg mb-2">{s.title}</h3>
            <p className="text-sm text-muted-foreground mb-6 flex-1">{s.description}</p>
            <Button className="rounded-md gap-2" onClick={() => setActive(s)} data-testid={`start-scenario-${s.id}`}>
              <Phone className="w-4 h-4" /> Start Call ({s.steps.length} turns)
            </Button>
          </div>
        ))}
      </div>

      {results.length > 0 && (
        <div className="p-6 rounded-md border border-border bg-card">
          <h3 className="font-heading font-bold text-lg mb-4">Your Recent Calls</h3>
          <div className="space-y-2" data-testid="ar-history">
            {results.slice(0, 8).map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.scenario_title}</p>
                  <p className="text-xs text-muted-foreground font-mono">Denial {r.denial_handling} · Comm {r.communication}</p>
                </div>
                <span className={`font-mono font-bold ${r.overall >= 75 ? "text-success" : r.overall >= 50 ? "text-chart-4" : "text-destructive"}`}>{r.overall}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CallSim({ scenario, onDone, onExit }) {
  const steps = scenario.steps;
  const [step, setStep] = useState(0);
  const [turns, setTurns] = useState([]); // {payer, student}
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [result, setResult] = useState(null);
  const [playingVoice, setPlayingVoice] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef();

  useEffect(() => () => clearInterval(timerRef.current), []);

  const playPayer = async () => {
    setPlayingVoice(true);
    try {
      const token = localStorage.getItem("pm_token");
      const res = await fetch(`${API}/ar-sim/tts/${scenario.id}/${step}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("tts failed");
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audio.onended = () => setPlayingVoice(false);
      audio.onerror = () => setPlayingVoice(false);
      await audio.play();
    } catch {
      toast.error("Could not play the payer voice.");
      setPlayingVoice(false);
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribeTurn(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Microphone access denied. Please allow mic permission.");
    }
  };

  const stop = () => {
    mediaRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const transcribeTurn = async (blob) => {
    setTranscribing(true);
    const fd = new FormData();
    fd.append("audio", blob, "turn.webm");
    try {
      const token = localStorage.getItem("pm_token");
      const res = await fetch(`${API}/ar-sim/transcribe`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error("failed");
      const json = await res.json();
      const newTurns = [...turns, { payer: steps[step].payer, student: json.transcript }];
      setTurns(newTurns);
      if (step + 1 < steps.length) {
        setStep(step + 1);
      } else {
        await scoreCall(newTurns);
      }
    } catch {
      toast.error("Could not transcribe. Try again.");
    } finally {
      setTranscribing(false);
    }
  };

  const scoreCall = async (allTurns) => {
    setScoring(true);
    try {
      const { data } = await api.post("/ar-sim/score", { scenario_id: scenario.id, turns: allTurns });
      setResult(data.result);
      onDone();
      toast.success("Call scored!");
    } catch {
      toast.error("Scoring failed. Try again.");
    } finally {
      setScoring(false);
    }
  };

  const restart = () => { setStep(0); setTurns([]); setResult(null); setSeconds(0); };

  if (scoring) {
    return (
      <div className="max-w-xl mx-auto text-center py-24" data-testid="ar-scoring">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Scoring your denial handling...</p>
        <div className="h-1 rounded-full tracing-beam mt-6" />
      </div>
    );
  }

  if (result) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="ar-result">
        <button onClick={onExit} className="text-sm text-muted-foreground hover:text-foreground mb-6">← Back to scenarios</button>
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3"><Trophy className="w-8 h-8 text-primary" /></div>
          <h2 className="font-heading text-4xl font-black">{result.overall}<span className="text-lg text-muted-foreground">/100</span></h2>
          <p className="text-muted-foreground">{scenario.title}</p>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6">
          <ScoreBox label="Denial Handling" value={result.denial_handling} testid="ar-score-denial" />
          <ScoreBox label="Professionalism" value={result.professionalism} testid="ar-score-prof" />
          <ScoreBox label="Communication" value={result.communication} testid="ar-score-comm" />
        </div>
        <div className="p-5 rounded-md border border-border bg-card mb-4">
          <p className="text-sm font-semibold mb-2">AI Feedback</p>
          <p className="text-sm text-muted-foreground leading-relaxed">{result.feedback}</p>
        </div>
        {result.strengths?.length > 0 && (
          <div className="p-5 rounded-md border border-border bg-card mb-4">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-success" /> Strengths</p>
            <ul className="space-y-2">{result.strengths.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-success">•</span>{t}</li>)}</ul>
          </div>
        )}
        {result.improvements?.length > 0 && (
          <div className="p-5 rounded-md border border-border bg-card mb-6">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-primary" /> Improve</p>
            <ul className="space-y-2">{result.improvements.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-primary">•</span>{t}</li>)}</ul>
          </div>
        )}
        <div className="flex gap-3">
          <Button variant="outline" className="rounded-md gap-2" onClick={restart} data-testid="ar-retry"><RotateCcw className="w-4 h-4" /> Retry Call</Button>
          <Button className="rounded-md" onClick={onExit} data-testid="ar-done">Done</Button>
        </div>
      </div>
    );
  }

  const current = steps[step];
  return (
    <div className="max-w-2xl mx-auto" data-testid="ar-call">
      <button onClick={onExit} className="text-sm text-muted-foreground hover:text-foreground mb-6">← End call</button>
      <div className="flex items-center justify-between mb-4">
        <span className="inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded bg-primary/10 text-primary"><PhoneCall className="w-3.5 h-3.5" /> {scenario.payer}</span>
        <span className="text-sm font-mono text-muted-foreground">Turn {step + 1} / {steps.length}</span>
      </div>
      <div className="h-1 rounded-full bg-secondary mb-8 overflow-hidden">
        <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${((step) / steps.length) * 100}%` }} />
      </div>

      {/* conversation so far */}
      <div className="space-y-3 mb-6" data-testid="ar-transcript">
        {turns.map((t, i) => (
          <div key={i} className="space-y-2">
            <div className="p-3 rounded-md bg-secondary/60 text-sm"><span className="font-mono text-xs text-muted-foreground">PAYER</span><p>{t.payer}</p></div>
            <div className="p-3 rounded-md border border-primary/30 bg-primary/5 text-sm ml-6"><span className="font-mono text-xs text-primary">YOU</span><p>{t.student}</p></div>
          </div>
        ))}
      </div>

      <div className="p-6 rounded-md border border-border bg-card mb-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Payer says</p>
          <Button variant="outline" size="sm" className="rounded-md gap-1.5 h-8" onClick={playPayer} disabled={playingVoice} data-testid="play-payer-voice">
            {playingVoice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />} Hear it
          </Button>
        </div>
        <p className="font-heading text-xl leading-relaxed mb-4">"{current.payer}"</p>
        <p className="text-sm text-muted-foreground flex items-start gap-2"><Lightbulb className="w-4 h-4 text-chart-4 shrink-0 mt-0.5" /> {current.hint}</p>
      </div>

      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          {recording && <div className="absolute inset-0 rounded-full bg-destructive/20 animate-ping" />}
          <button
            onClick={recording ? stop : start}
            disabled={transcribing}
            data-testid="ar-record-button"
            className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 ${recording ? "bg-destructive text-white" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
          >
            {transcribing ? <Loader2 className="w-7 h-7 animate-spin" /> : recording ? <Square className="w-7 h-7" /> : <Mic className="w-8 h-8" />}
          </button>
        </div>
        <p className="font-mono text-sm text-muted-foreground" data-testid="ar-record-status">
          {transcribing ? "Transcribing your response..." : recording ? `Recording... ${seconds}s — tap to send` : "Tap to record your response"}
        </p>
      </div>
    </div>
  );
}

function ScoreBox({ label, value, testid }) {
  return (
    <div className="p-4 rounded-md border border-border bg-card text-center" data-testid={testid}>
      <p className="font-mono text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
